import { useState, useCallback, useMemo } from 'react';
import { DROP_POSITION } from '../constants';

export const useHierarchyDragDrop = ({
    enabled,
    selectedIds,
    nodeMap,
    onDrop,
    canDrop,
    externalNodes = [],
    allExternalNodes,
    selectedExternalIds = [],
}) => {
    const [activeId, setActiveId] = useState(null);
    const [overId, setOverId] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dropPosition, setDropPosition] = useState(DROP_POSITION.INSIDE);

    // Use the full (unfiltered) external node list when available so that items
    // selected before a search filter is applied can still be resolved and dragged.
    const externalNodesForMap = allExternalNodes || externalNodes;

    // Create combined nodeMap including external nodes
    const combinedNodeMap = useMemo(() => {
        const combined = { ...nodeMap };
        externalNodesForMap.forEach(node => {
            combined[node.id] = node;
        });
        return combined;
    }, [nodeMap, externalNodesForMap]);

    // Get nodes being dragged (supports multi-select)
    const getDraggedItems = useCallback(() => {
        if (!activeId) {
            return [];
        }

        // Check if dragging external node
        const isExternalNode = externalNodesForMap.some(node => node.id === activeId);

        if (isExternalNode) {
            // If active external node is in selection, drag all selected external nodes
            if (selectedExternalIds.includes(activeId)) {
                // Resolve every selected id against the full node map; guard against
                // any id that has no matching node.
                return selectedExternalIds.map(id => combinedNodeMap[id]).filter(Boolean);
            }
            // Otherwise, drag only the active external node
            const activeNode = combinedNodeMap[activeId];
            if (activeNode) {
                return [activeNode];
            }
        } else {
            // Standard hierarchy node dragging
            // If active node is in selection, drag all selected nodes
            if (selectedIds.includes(activeId)) {
                return selectedIds.map(id => combinedNodeMap[id]).filter(Boolean);
            }
            // Otherwise, drag only the active node
            const activeNode = combinedNodeMap[activeId];
            if (activeNode) {
                return [activeNode];
            }
        }

        return [];
    }, [activeId, selectedIds, selectedExternalIds, externalNodesForMap, combinedNodeMap]);

    // Handle drag start
    const handleDragStart = useCallback((event) => {
        if (!enabled) {
            return;
        }
        const { active } = event;
        setActiveId(active.id);
        setIsDragging(true);
    }, [enabled]);

    // Handle drag over with position calculation
    const handleDragOver = useCallback((event) => {
        if (!enabled) {
            return;
        }

        const { over } = event;

        if (!over) {
            setOverId(null);
            setDropPosition(DROP_POSITION.INSIDE);
            return;
        }

        setOverId(over.id);

        // Calculate drop position based on cursor position
        const targetNode = combinedNodeMap[over.id];
        if (targetNode) {
            // Try to get the target node's DOM element rect
            const targetElement = document.querySelector(`[data-id="${over.id}"]`);

            if (targetElement && event.activatorEvent) {
                const targetRect = targetElement.getBoundingClientRect();
                const relativeY = event.activatorEvent.clientY - targetRect.top;
                const heightPercent = relativeY / targetRect.height;

                // Top 25% = BEFORE, Bottom 25% = AFTER, Middle 50% = INSIDE
                if (heightPercent < 0.25) {
                    setDropPosition(DROP_POSITION.BEFORE);
                } else if (heightPercent > 0.75) {
                    setDropPosition(DROP_POSITION.AFTER);
                } else {
                    // Middle area - drop inside the target node
                    setDropPosition(DROP_POSITION.INSIDE);
                }
            }
        }
    }, [enabled, combinedNodeMap]);

    // Handle drag cancel
    const handleDragCancel = useCallback(() => {
        setActiveId(null);
        setOverId(null);
        setIsDragging(false);
        setDropPosition(DROP_POSITION.INSIDE);
    }, []);

    // Handle drag end
    const handleDragEnd = useCallback((event) => {
        if (!enabled) {
            return;
        }

        const { active, over } = event;

        if (over && active.id !== over.id) {
            const draggedItems = getDraggedItems();
            const targetNode = combinedNodeMap[over.id];

            if (draggedItems.length > 0 && targetNode) {
                // Validate drop with site boundary check
                const canDropHere = canDrop(combinedNodeMap, draggedItems, targetNode, dropPosition);

                if (canDropHere && onDrop) {
                    onDrop(draggedItems, targetNode, dropPosition);
                }
            }
        }

        // Reset state
        handleDragCancel();
    }, [enabled, getDraggedItems, combinedNodeMap, canDrop, onDrop, dropPosition]);

    return {
        activeId,
        overId,
        isDragging,
        dropPosition,
        handleDragStart,
        handleDragOver,
        handleDragEnd,
        handleDragCancel,
        getDraggedItems,
        setDropPosition,
    };
};
